import React, { useCallback, forwardRef, useImperativeHandle, useState } from "react";
import { useDropzone, DropzoneInputProps } from "react-dropzone";
import classNames from "classnames";
import { IconButton } from "src/components/IconButton/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { Alert, Box, Grid, Theme, Typography } from "@mui/material";

interface IFileUploadProps {
  onFile?: (file: File, fileContents: ArrayBuffer) => void;
  handleRemoveFile?: () => void;
  dropMessage?: string;
  dropZoneInputProps?: DropzoneInputProps;
  "data-testid"?: string;
}
const FileUploadStyles = (theme: Theme): any => ({
  root: {
    display: "flex",
    cursor: "pointer",
    minHeight: "100px",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    flexGrow: 1,
    backgroundColor: theme.custom.forms.inputBack, //theme.palette.background.paper,
    borderRadius: "3px",
    padding: "4px",
    border: `2x solid #${theme.custom.forms.inputBorder}`,
    width: "100%",
    "& .title": { fontSize: 18, fontWeight: 700 },
    "& .body": {
      display: "flex",
      border: `2x solid ${theme.custom.forms.inputBorder}`,
      minHeight: "100px",
      alignItems: "center",
      color: theme.custom.forms.focusedColor,
      fontSize: 12,
      fontWeight: 300,
      textAlign: "center",
      padding: "4px 8px",
      opacity: 0.5,
      userSelect: "none",
    },
    "&.disabled": {
      opacity: 0.5,
      userSelect: "none",
      pointerEvents: "none",
    },
    "&.reject": {
      backgroundcolor: theme.custom.forms.errorColor,
    },
    "&.active": {
      backgroundcolor: theme.custom.forms.focusedColor,
    },
    "&.dirty": {
      backgroundcolor: theme.custom.forms.dirtyColor,
    },
    "&.over": {
      backgroundcolor: theme.custom.forms.dirtyColor,
    },
  },
  uploadDiv: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: "2px",
    borderRadius: "10px",
    minHeight: "100px",
    borderColor: theme.custom.forms.inputBorder,
    borderStyle: "solid",
    backgroundColor: "#acacac29",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
    height: "20%",
    margin: "20px",
    textAlign: "center",
  },
  reject: {
    borderColor: "red",
  },
  accept: {
    borderColor: "green",
  },
  active: {
    borderColor: "yellow",
  },
  fileList: {
    color: "white",
    width: "100%",
    fontSize: "0.9em",
    marginLeft: 2,
    marginTop: 1,
  },
  fileListDiv: {
    width: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  removeFileBtn: {
    padding: 0,
    margin: "5px",
    "& svg": {
      width: "16px",
      height: "16px",
    },
  },
});
export const FileUpload = forwardRef((props: IFileUploadProps, ref: any): JSX.Element => {
  const { onFile, handleRemoveFile, dropMessage, dropZoneInputProps, "data-testid": dataTestId } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [fileListLengthError, setFileListLengthError] = useState<string[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFiles([]);
      setFileListLengthError([]);
      setFiles(acceptedFiles);
      acceptedFiles.forEach((file: File) => {
        const reader = new FileReader();
        reader.onabort = () => console.log("File reading was aborted");
        reader.onerror = () => console.log("File reading has failed");
        reader.onload = () => {
          const binaryStr = reader.result as ArrayBuffer;
          if (onFile) {
            onFile(file, binaryStr);
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },
    [onFile]
  );

  // Handle rejected files and errors
  const onDropRejected = useCallback((fileRejections: any) => {
    const errors: string[] = [];

    fileRejections.forEach(({ file, errors: fileErrors }: { file: any; errors: any }) => {
      fileErrors.forEach((error: any) => {
        if (error.code === "file-too-large") {
          errors.push(`Error: File "${file.name}" is too large.`);
        } else if (error.code === "file-invalid-type") {
          errors.push(`Error: File "${file.name}" has an invalid file type.`);
        } else {
          errors.push(`Error uploading "${file.name}": ${error.message}`);
        }
      });
    });

    setFileListLengthError(errors);
  }, []);
  const maxSizeInMB = 50;
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    maxSize: maxSizeInMB * 1024 * 1024,
    maxFiles: 1,
    onDrop,
    onDropRejected,
  });

  const injectedInputProps = dropZoneInputProps ?? {};
  const inputProps = { ...getInputProps(), ...injectedInputProps, ...(dataTestId ? { "data-testid": dataTestId } : {})};

  useImperativeHandle(ref, () => ({
    removeAllFiles() {
      setFiles([]);
    },
  }));

  const removeFile = useCallback(
    (fileIndex: number) => (event: React.MouseEvent) => {
      event.stopPropagation();
      if (handleRemoveFile) {
        handleRemoveFile();
      }
      setFiles((prevFiles) => prevFiles.filter((_, index) => index !== fileIndex));
    },
    [handleRemoveFile]
  );

  const filesList = files.map((file: File, index: number) => (
    <Grid container alignItems="center" key={index}>
      <IconButton sx={(theme) => FileUploadStyles(theme).removeFileBtn} onClick={removeFile(index)} size="large">
        <DeleteIcon />
      </IconButton>

      <Typography color="textPrimary">{file.name}</Typography>
    </Grid>
  ));

  const showFileList = files.length > 0;

  return (
    <>
      {fileListLengthError.length > 0 && (
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
          {fileListLengthError.map((err, index) => {
            return <Box key={index}>{err}</Box>;
          })}
        </Alert>
      )}
      <Box
        {...getRootProps()}
        className={classNames("active", isDragReject && "reject", isDragAccept && "over")}
        sx={(theme) => FileUploadStyles(theme).root}
      >
        <input {...inputProps} />

        <Box className="body">
          <Grid container>
            <Grid item xs={12}>
              {isDragActive
                ? "Drop file here ..."
                : (dropMessage ??
                  "Drag file containing events you wish to import here or click to open file browser. (Max file size 9 MB)")}
            </Grid>
            <Grid item xs={12} sx={(theme) => FileUploadStyles(theme).fileList}>
              {showFileList && filesList}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
});
